import {
  type Address,
  type SubscriptionAttributes,
  type Visitor,
} from '@bbkPricingPage/types/pricing-table-types';

export const EventKeys = {
  SET_VISITOR: 'pricify.hosted.page.setVisitor',
  SET_QUANTITY: 'pricify.hosted.page.setQuantity',
  SET_BILLING_ADDRESS: 'pricify.hosted.page.setBillingAddress',
  SET_SHIPPING_ADDRESS: 'pricify.hosted.page.setShippingAddress',
  SET_BUSINESS_ENTITY: 'pricify.hosted.page.setBusinessEntity',
  SET_CURRENCY: 'pricify.hosted.page.setCurrency',
  SET_PRICE_VARIANT: 'pricify.hosted.page.setPriceVariant',
  SET_SUBSCRIPTION_ATTRIBUTES: 'pricify.hosted.page.setSubscriptionAttributes',
  PAGE_LOAD: 'pricify.hosted.page.load',
  DIMENSION: 'pricify.hosted.page.dimension',
  SHOW_TOTAL_PRICE: 'pricify.hosted.page.showTotalPrice',
  CTA_CLICK: 'pricify.hosted.page.ctaClick',
  SHOW_LOADER: 'pricify.hosted.page.showLoader',
};

export const CbEventKeys = {
  SUCCESS: 'cb.success',
};

export type EventData = {
  source: 'pricify';
  type: EventType;
  payload: EventPayload;
};

export type EventOptions = {
  host: string;
  pricingPageKey?: string;
  sessionId?: string;
};

export type EventType = (typeof EventKeys)[keyof typeof EventKeys];

export type EventPayload =
  | SetVisitorEvent
  | SetQuantityEvent
  | SetBillingAddressEvent
  | SetShippingAddressEvent
  | SetBusinessEntityEvent
  | SetCurrencyEvent
  | SetPriceVariantEvent
  | SetSubscriptionAttributesEvent
  | CbSuccessEvent
  | PageLoadEvent
  | DimensionEvent
  | SetShowPriceEvent
  | CtaClickEvent
  | SetShowLoaderEvent;

type BaseEvent = {
  siteId?: string;
  pricingPageId?: string;
  sessionId?: string;
};

export type SetVisitorEvent = Visitor & BaseEvent;

export type SetSubscriptionAttributesEvent = SubscriptionAttributes & BaseEvent;

export type SetQuantityEvent = {
  quantity?: number;
} & BaseEvent;

export type SetBusinessEntityEvent = {
  id?: string;
} & BaseEvent;

export type SetBillingAddressEvent = Address & BaseEvent;

export type SetShippingAddressEvent = Address & BaseEvent;

export type CbSuccessEvent = {
  value?: string;
  customerId?: string;
  redirectUrl?: string;
  data?: unknown;
} & BaseEvent;

export type SetCurrencyEvent = {
  currency?: string;
} & BaseEvent;

export type SetPriceVariantEvent = {
  id?: string;
} & BaseEvent;

export type PricingPageHostEventMetadata = {
  pricingPageKey: string;
  /** Pricing page origin (e.g. https://app.example.com) for parent postMessage targetOrigin */
  urlDomain: string;
  sessionId?: string;
  legacySiteId?: string;
  legacyPricingPageId?: string;
};

export type PageLoadEvent = {
  pricingPage?: unknown;
} & PricingPageHostEventMetadata;

export type DimensionEvent = {
  window: {
    height: number;
    width: number;
  };
  scroll: {
    height: number;
    width: number;
  };
} & PricingPageHostEventMetadata;

export type ShowPriceConfig = {
  prefix?: string;
  periodUnit?: string;
  period?: number;
  suffix?: string;
};

export type ShowPrice = {
  configs?: ShowPriceConfig[];
};

export type SetShowPriceEvent = ShowPrice & BaseEvent;

/** Optional copy for the merchant-controlled full-page loader overlay. */
export type LoaderData = {
  header?: string;
  subheader?: string;
};

/**
 * Merchant-controlled full-page loader overlay, toggled via cb-pricify-js `Pricify.showLoader(arg)`.
 * `true` or `{ header?, subheader? }` shows the overlay; `false` or `null` hides it. The handler
 * on this side is idempotent (re-applying the same value is a no-op).
 */
export type SetShowLoaderEvent = {
  show?: boolean;
} & LoaderData &
  BaseEvent;

// ----- CTA CLICK (js-callback) -----
// Emitted when a plan whose primary CTA is configured as `js_callback` is clicked. The iframe only
// notifies the parent (cb-pricify-js) and stops — no Chargebee checkout, no redirect.
// Canonical payload spec: cb-pricify-js `BNI-req/work-progress/payload-spec.md`.

/** Where the CTA was clicked from. */
export enum CtaSource {
  card = 'card',
  order_summary = 'order_summary',
}

/** Line-item classification carried alongside the selected plan price. */
export enum CtaLineItemType {
  mandatory_addon = 'mandatory_addon',
  recommended_addon = 'recommended_addon',
  charge = 'charge',
}

export type CtaPriceFrequency = {
  period?: number;
  periodUnit?: string;
};

export type CtaLineItem = {
  type: CtaLineItemType;
  /** Resolved price id (`price.billing_system_id`), matches subscription item `billing_system_item_price_id`. */
  billingSystemItemPriceId: string;
  /** Omitted for `flat_fee` prices, which have no meaningful quantity. */
  quantity?: number;
  currency?: string;
  /** Omitted for one-time charges. */
  priceFrequency?: CtaPriceFrequency;
};

export type CtaConfig = {
  ctaSource: CtaSource;
  /** Resolved price id (`price.billing_system_id`), matches subscription item `billing_system_item_price_id`. */
  billingSystemItemPriceId: string;
  /** Parent item id the resolved price belongs to (`price.billing_system_item_id`). */
  billingSystemItemId?: string;
  /** Omitted for `flat_fee` prices, which have no meaningful quantity. */
  quantity?: number;
  currency?: string;
  priceFrequency?: CtaPriceFrequency;
  pricingModel?: string;
  isSubscribedPrice: boolean;
  lineItems: CtaLineItem[];
};

export type CtaClickEvent = {
  event: 'pricing_page_cta_clicked';
  sessionId?: string;
  customerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  ctaConfig: CtaConfig;
  /** Routing key the parent (cb-pricify-js) uses for Growth pages. */
  pricingPageKey?: string;
};
